/*
 * cornhole-configurator.js
 * Gedeelde engine voor de custom-configurator (cornhole-skin voor het WW-thema).
 * Superset van de BLNC-engine: + zakjes-contributor + data-cart-mode.
 * Geladen als <script type="module">.
 */

/* ---------- Pure prijsfunctie (getest) ---------- */
function computeTotal(state) {
  var total = (state && state.variantPrice) || 0;
  var c = (state && state.choices) || {};
  Object.keys(c).forEach(function (k) { if (c[k] && c[k].price) total += c[k].price; });
  ((state && state.upsells) || []).forEach(function (u) { if (u && u.price) total += u.price; });
  ((state && state.zakjes) || []).forEach(function (z) { if (z && z.price) total += z.price; });
  if (state && state.express && state.express.price) total += state.express.price;
  return total;
}

/* ---------- Meest gekozen zakjes-categorie (getest) ---------- */
/*
 * Bepaalt welke zakjes-categorie het "meest gekozen"-label krijgt, op basis van
 * de boarddikte. Die dikte staat bij de meeste sets in de variant-titel
 * ("10mm", "15mm COMP") en bij sets met één variant in de producttitel
 * ("SURF - Cornhole Set - 12mm"); vandaar dat beide teksten worden meegegeven.
 *
 * Regel (besluit Tjarco 31-7-2026):
 *   COMP / ELITE -> Pro
 *   15mm / 12mm  -> Premium
 *   10mm         -> Stick & Slide (double sided)
 *
 * COMP wint van de dikte: "15mm COMP" is een pro-set, geen premium-set.
 * Geeft null terug als er niets te bepalen valt of als de doelcategorie op dit
 * product niet bestaat — de aanroeper laat dan de bestaande default staan.
 */
function bepaalMeestGekozen(tekst, beschikbareCategorieen) {
  var t = String(tekst || '').toLowerCase();
  var cats = beschikbareCategorieen || [];
  function kies(cat) { return cats.indexOf(cat) === -1 ? null : cat; }

  // \b voorkomt dat "Complete Cornhole Set" als COMP-set wordt gelezen.
  if (/\bcomp\b/.test(t) || /\belite\b/.test(t)) return kies('pro');
  if (/\b(12|15|18)\s*mm\b/.test(t)) return kies('premium');
  if (/\b10\s*mm\b/.test(t)) return kies('slide');
  return null;
}

export { computeTotal, bepaalMeestGekozen };

/* ---------- Browser-controller ---------- */
function formatMoney(cents) {
  var amount = (cents / 100);
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  } catch (e) {
    return '€ ' + amount.toFixed(2).replace('.', ',');
  }
}

function readNum(el, attr) {
  var v = el && el.getAttribute(attr);
  var n = parseInt(v, 10);
  return isNaN(n) ? 0 : n;
}

function Configurator(root) {
  this.root = root;
  this.uid = root.getAttribute('data-uid') || String(root.id || 'cfg');
  this.cartMode = root.getAttribute('data-cart-mode') || 'horizon';
  this.totalEl = root.querySelector('[data-blnc-total]');
  this.atcBtn = root.querySelector('[data-blnc-atc]');
  this.fileInput = root.querySelector('[data-blnc-file]');
  this.qtyInput = root.querySelector('[data-blnc-qty]');
  this.state = { variantId: null, variantPrice: 0, choices: {}, upsells: [], zakjes: [], express: null };
  this.init();
}

Configurator.prototype.init = function () {
  var self = this;

  var selVariant = this.root.querySelector('.blnc-cfg__variant.is-sel') || this.root.querySelector('.blnc-cfg__variant');
  if (selVariant) {
    this.selectVariant(selVariant, false);
  } else {
    // Geen variant-laag (bv. product met 1 variant): pak de basisvariant uit root-data
    this.state.variantId = readNum(this.root, 'data-default-variant-id') || null;
    this.state.variantPrice = readNum(this.root, 'data-default-variant-price');
  }

  this.root.querySelectorAll('.blnc-cfg__choice input[type="radio"]:checked').forEach(function (r) { self.applyChoice(r, false); });

  this.root.querySelectorAll('.blnc-cfg__variant').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (btn.getAttribute('data-available') === 'false') return;
      self.selectVariant(btn, true);
      self.advanceFrom(btn);
    });
  });

  this.root.querySelectorAll('.blnc-cfg__choice input[type="radio"]').forEach(function (r) {
    r.addEventListener('change', function () { self.applyChoice(r, true); self.advanceFrom(r); });
  });

  this.root.querySelectorAll('.blnc-cfg__addon input[type="checkbox"], .blnc-cfg__express input[type="checkbox"]').forEach(function (cb) {
    cb.addEventListener('change', function () { self.recomputeAddons(); self.render(); });
  });

  // Zakjes: gedelegeerd zodat dynamisch toegevoegde/verwijderde sets ook meetellen in het totaal.
  this.root.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('.cfg-zakjes [data-blnc-zak]')) {
      self.recomputeZakjes(); self.render();
    }
  });
  // Signaal bij toevoegen/verwijderen van een set (geen [data-blnc-zak]-change beschikbaar).
  this.root.addEventListener('blnc:zakjes-changed', function () { self.recomputeZakjes(); self.render(); });

  this.root.querySelectorAll('[data-blnc-required]').forEach(function (inp) {
    inp.addEventListener('change', function () {
      if (inp.value) {
        var setEl = inp.closest('.cfg-zakjes__set');
        if (setEl) setEl.classList.remove('is-invalid');
        var errEl = self.root.querySelector('[data-blnc-error]');
        if (errEl) errEl.hidden = true;
      }
    });
  });

  this.root.querySelectorAll('.blnc-cfg__head').forEach(function (head) {
    head.addEventListener('click', function () {
      var row = head.closest('.blnc-cfg__row');
      var isOpen = row.getAttribute('data-open') === 'true';
      self.closeAll();
      if (!isOpen) self.openRow(row);
    });
  });

  this.root.querySelectorAll('[data-blnc-next]').forEach(function (b) {
    b.addEventListener('click', function () {
      var row = b.closest('.blnc-cfg__row');
      self.closeAll();
      var next = self.nextRow(row);
      if (next) { self.openRow(next); self.scrollToRow(next); }
    });
  });

  var first = this.root.querySelector('.blnc-cfg__row');
  if (first) this.openRow(first);

  if (this.qtyInput) this.qtyInput.addEventListener('input', function () { self.render(); });

  if (this.atcBtn) this.atcBtn.addEventListener('click', function () { self.addToCart(); });

  if (this.fileInput) {
    this.fileInput.addEventListener('change', function () {
      var nameEl = self.root.querySelector('[data-blnc-file-name]');
      if (nameEl && self.fileInput.files && self.fileInput.files.length) {
        nameEl.textContent = self.fileInput.files[0].name;
      }
    });
  }

  this.render();
  this.volgHandmatigeCategoriekeuze();
  this.updateMeestGekozen();
};

Configurator.prototype.selectVariant = function (btn, updateMedia) {
  this.root.querySelectorAll('.blnc-cfg__variant').forEach(function (b) { b.classList.remove('is-sel'); });
  btn.classList.add('is-sel');
  this.state.variantId = readNum(btn, 'data-variant-id');
  this.state.variantPrice = readNum(btn, 'data-price');
  this.setHeadValue(btn, btn.getAttribute('data-label') || '');
  if (updateMedia) this.switchGalleryMedia(btn.getAttribute('data-media-id'), btn.getAttribute('data-image'));
  this.updateMeestGekozen();
  this.render();
};

/*
 * Verplaatst de "meest gekozen"-badge naar de categorie die bij de boarddikte van
 * de gekozen variant hoort, en selecteert die categorie voor — maar alleen zolang
 * de klant nog niet zelf een categorie heeft aangeklikt. Wie bewust Comp kiest en
 * daarna van dikte wisselt, moet niet terug naar Premium gegooid worden.
 */
Configurator.prototype.updateMeestGekozen = function () {
  var selBtn = this.root.querySelector('.blnc-cfg__variant.is-sel');
  var variantTitel = selBtn ? (selBtn.getAttribute('data-label') || '') : '';
  var tekst = variantTitel + ' ' + (this.root.getAttribute('data-product-title') || '');

  this.root.querySelectorAll('.cfg-zakjes__set').forEach(function (setEl) {
    var knoppen = Array.prototype.slice.call(setEl.querySelectorAll('.cfg-zakjes__cat-btn'));
    if (!knoppen.length) return;
    var cats = knoppen.map(function (b) { return b.getAttribute('data-cat'); });
    var doel = bepaalMeestGekozen(tekst, cats);

    knoppen.forEach(function (b) {
      var badge = b.querySelector('.cfg-zakjes__cat-badge');
      if (badge) badge.hidden = b.getAttribute('data-cat') !== doel;
    });

    if (!doel || setEl.dataset.blncUserPicked === '1') return;
    knoppen.forEach(function (b) {
      if (b.getAttribute('data-cat') === doel && !b.classList.contains('is-active')) b.click();
    });
  });
};

/*
 * Onthoudt of de klant zélf een zakjes-categorie koos. isTrusted is false bij een
 * programmatische .click(), dus onze eigen voorselectie telt hier niet mee.
 */
Configurator.prototype.volgHandmatigeCategoriekeuze = function () {
  this.root.addEventListener('click', function (e) {
    if (!e.isTrusted) return;
    var btn = e.target.closest && e.target.closest('.cfg-zakjes__cat-btn');
    if (!btn) return;
    var setEl = btn.closest('.cfg-zakjes__set');
    if (setEl) setEl.dataset.blncUserPicked = '1';
  }, true);
};

Configurator.prototype.switchGalleryMedia = function (mediaId, imgUrl) {
  // Focal main-product gallery: klik de nav-knop met dit media-id (triggert de eigen slider)
  if (mediaId) {
    var navBtn = document.querySelector('[data-action="select"][data-media-id="' + mediaId + '"]');
    if (navBtn) { navBtn.click(); return; }
  }
  // Horizon <media-gallery>: haal de gallery-sectie voor deze variant op (section render) en vervang 'm.
  var gallery = document.querySelector('media-gallery');
  if (gallery && this.state.variantId) {
    var section = gallery.closest('[id^="shopify-section-"]');
    var sectionId = section ? section.id.replace('shopify-section-', '') : null;
    if (sectionId) {
      var vid = this.state.variantId;
      fetch(location.pathname + '?variant=' + vid + '&section_id=' + sectionId, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('media-gallery');
          var cur = document.querySelector('media-gallery');
          if (fresh && cur) cur.replaceWith(fresh);
        })
        .catch(function () {});
      return;
    }
  }
  // Fallback: eigen media-afbeelding (indien aanwezig)
  if (imgUrl) {
    var main = document.querySelector('[data-blnc-media-main]');
    if (main) main.setAttribute('src', imgUrl);
  }
};

Configurator.prototype.applyChoice = function (radio, doRender) {
  var row = radio.closest('.blnc-cfg__row');
  var key = row.getAttribute('data-layer');
  this.state.choices[key] = {
    id: readNum(radio, 'data-variant-id') || null,
    price: readNum(radio, 'data-price'),
    label: radio.getAttribute('data-label') || ''
  };
  this.setHeadValue(radio, radio.getAttribute('data-label') || '');
  if (doRender) this.render();
};

Configurator.prototype.recomputeAddons = function () {
  var ups = [];
  this.root.querySelectorAll('.blnc-cfg__addon input[type="checkbox"]:checked').forEach(function (cb) {
    ups.push({ id: readNum(cb, 'data-variant-id'), price: readNum(cb, 'data-price'), label: cb.getAttribute('data-label') || '' });
  });
  this.state.upsells = ups;
  var exp = this.root.querySelector('.blnc-cfg__express input[type="checkbox"]:checked');
  this.state.express = exp
    ? { id: readNum(exp, 'data-variant-id'), price: readNum(exp, 'data-price'), label: exp.getAttribute('data-label') || '' }
    : null;
  this.updateAddonHeads();
};

// Toon gekozen labels van checkbox-lagen (afwerking / accessoires+opties) in de rij-kop
Configurator.prototype.updateAddonHeads = function () {
  this.root.querySelectorAll('.blnc-cfg__row').forEach(function (row) {
    if (!row.querySelector('.blnc-cfg__addon input[type="checkbox"]')) return;
    var labels = [];
    row.querySelectorAll('.blnc-cfg__addon input[type="checkbox"]:checked').forEach(function (cb) {
      labels.push(cb.getAttribute('data-label') || '');
    });
    var val = row.querySelector('[data-blnc-value]');
    if (val) val.textContent = labels.join(', ');
  });
};

Configurator.prototype.recomputeZakjes = function () {
  var zak = [];
  this.root.querySelectorAll('.cfg-zakjes [data-blnc-zak]').forEach(function (inp) {
    if (inp.value) {
      zak.push({
        id: parseInt(inp.value, 10),
        price: parseInt(inp.getAttribute('data-price'), 10) || 0,
        label: inp.getAttribute('data-label') || '',
        category: inp.getAttribute('data-category') || ''
      });
    }
  });
  this.state.zakjes = zak;
  // Toon aantal gekozen sets in de rij-kop
  var row = this.root.querySelector('.blnc-cfg__row[data-layer="zakjes"]');
  if (row) {
    var val = row.querySelector('[data-blnc-value]');
    if (val) val.textContent = zak.length ? (zak.length + ' set' + (zak.length > 1 ? 's' : '')) : '';
  }
};

Configurator.prototype.setHeadValue = function (el, value) {
  var row = el.closest('.blnc-cfg__row');
  if (!row) return;
  var valEl = row.querySelector('[data-blnc-value]');
  if (valEl) valEl.textContent = value;
};

Configurator.prototype.swapMedia = function (src) {
  if (!src) return;
  var main = this.root.querySelector('[data-blnc-media-main]');
  if (main) main.setAttribute('src', src);
};

Configurator.prototype.openRow = function (row) {
  row.setAttribute('data-open', 'true');
  var head = row.querySelector('.blnc-cfg__head');
  if (head) head.setAttribute('aria-expanded', 'true');
  var body = row.querySelector('.blnc-cfg__body');
  if (body) body.style.display = 'block';
};

Configurator.prototype.closeAll = function () {
  this.root.querySelectorAll('.blnc-cfg__row').forEach(function (row) {
    row.setAttribute('data-open', 'false');
    var head = row.querySelector('.blnc-cfg__head');
    if (head) head.setAttribute('aria-expanded', 'false');
    var body = row.querySelector('.blnc-cfg__body');
    if (body) body.style.display = 'none';
  });
};

Configurator.prototype.nextRow = function (row) {
  var rows = Array.prototype.slice.call(this.root.querySelectorAll('.blnc-cfg__row'));
  var idx = rows.indexOf(row);
  return idx > -1 && idx < rows.length - 1 ? rows[idx + 1] : null;
};

Configurator.prototype.advanceFrom = function (el) {
  var row = el.closest('.blnc-cfg__row');
  if (!row || row.getAttribute('data-advance') !== 'true') return;
  var self = this;
  row.classList.add('is-just-chosen');
  setTimeout(function () {
    row.classList.remove('is-just-chosen');
    self.closeAll();
    var next = self.nextRow(row);
    if (next) { self.openRow(next); self.scrollToRow(next); }
  }, 260);
};

// Scroll de zojuist geopende laag in beeld (scroll-margin-top in CSS houdt 'm onder de sticky header).
Configurator.prototype.scrollToRow = function (row) {
  if (!row || !row.scrollIntoView) return;
  setTimeout(function () { row.scrollIntoView({ block: 'start' }); }, 40);
};

Configurator.prototype.getQty = function () {
  var q = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
  return (isNaN(q) || q < 1) ? 1 : q;
};

Configurator.prototype.render = function () {
  if (this.totalEl) this.totalEl.textContent = formatMoney(computeTotal(this.state) * this.getQty());
};

Configurator.prototype.readableProps = function () {
  var props = {};
  var labels = { print: 'Print', painted: 'Painted White', finish: 'Afwerking', stoppers: 'Stoppers' };
  var c = this.state.choices;
  Object.keys(c).forEach(function (k) { if (c[k] && c[k].id && c[k].label) props[labels[k] || k] = c[k].label; });
  this.state.zakjes.forEach(function (z, i) { props['Zakjes ' + (i + 1)] = z.label; });
  if (this.state.express) props['Express'] = 'Ja';
  props['_config'] = this.uid;
  return props;
};

Configurator.prototype.addonItems = function (qty) {
  var items = [];
  var uid = this.uid;
  var q = qty || 1;
  var c = this.state.choices;
  Object.keys(c).forEach(function (k) { if (c[k] && c[k].id) items.push({ id: c[k].id, quantity: q, properties: { _config: uid } }); });
  // Custom-zakjes worden apart via multipart toegevoegd (eigen Design-bestand) — hier overslaan.
  this.state.zakjes.forEach(function (z) { if (z.id && z.category !== 'custom') items.push({ id: z.id, quantity: q, properties: { _config: uid } }); });
  this.state.upsells.forEach(function (u) { if (u.id) items.push({ id: u.id, quantity: q, properties: { _config: uid } }); });
  if (this.state.express && this.state.express.id) items.push({ id: this.state.express.id, quantity: q, properties: { _config: uid } });
  return items;
};

// Custom-zakjes-sets: eigen cart-regel met properties[Design] = de per-set upload.
Configurator.prototype.customBagSets = function () {
  var out = [];
  this.root.querySelectorAll('.cfg-zakjes__set').forEach(function (setEl) {
    var inp = setEl.querySelector('[data-blnc-zak]');
    if (!inp || !inp.value) return;
    if (inp.getAttribute('data-category') !== 'custom') return;
    var file = setEl.querySelector('[data-blnc-zak-file]');
    out.push({ id: parseInt(inp.value, 10), file: (file && file.files && file.files.length) ? file.files[0] : null });
  });
  return out;
};

Configurator.prototype.addToCart = function () {
  var self = this;
  if (!this.state.variantId) return;

  // Verplichte velden (bv. eerste zakjes-set)
  var invalid = [];
  this.root.querySelectorAll('[data-blnc-required]').forEach(function (inp) {
    if (!inp.value) {
      var setEl = inp.closest('.cfg-zakjes__set');
      if (setEl) setEl.classList.add('is-invalid');
      invalid.push(inp);
    }
  });
  // Custom-upload is optioneel (design mag ook later aangeleverd worden) → geen bestand-validatie.
  if (invalid.length) {
    var firstInvalid = invalid[0];
    var row = firstInvalid.closest('.blnc-cfg__row');
    if (row) { this.closeAll(); this.openRow(row); }
    var errEl = this.root.querySelector('[data-blnc-error]');
    if (errEl && this.atcBtn) { errEl.textContent = this.atcBtn.getAttribute('data-required-msg') || ''; errEl.hidden = false; }
    // Scroll naar de eerste ontbrekende (rode) set, zodat de klant ziet wat er nog moet
    var target = firstInvalid.closest('.cfg-zakjes__set') || row;
    if (target && target.scrollIntoView) {
      setTimeout(function () { target.scrollIntoView({ block: 'center' }); }, 90);
    }
    return;
  }

  var btn = this.atcBtn;
  var original = btn ? btn.textContent : '';
  var loadingText = (btn && btn.getAttribute('data-loading')) || 'Even geduld…';
  if (btn) { btn.disabled = true; btn.textContent = loadingText; }

  var qty = this.getQty();

  // Hoofdproduct (board) + eventueel board-design als multipart-add.
  var mainFd = new FormData();
  mainFd.append('id', String(this.state.variantId));
  mainFd.append('quantity', String(qty));
  var props = this.readableProps();
  Object.keys(props).forEach(function (k) { mainFd.append('properties[' + k + ']', props[k]); });
  if (this.fileInput && this.fileInput.files && this.fileInput.files.length) {
    mainFd.append('properties[Design]', this.fileInput.files[0]);
  }

  // Elke custom-zakjes-set: eigen multipart-add met properties[Design] = die set z'n upload.
  var customForms = this.customBagSets().map(function (cs) {
    var fd = new FormData();
    fd.append('id', String(cs.id));
    fd.append('quantity', String(qty));
    fd.append('properties[_config]', self.uid);
    if (cs.file) fd.append('properties[Design]', cs.file);
    return fd;
  });

  function postForm(fd) {
    return fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd })
      .then(function (r) { return r.json(); })
      .then(function (res) { if (res && res.status && res.status !== 200) throw new Error(res.description || 'cart error'); return res; });
  }

  // Sequentieel: hoofdproduct → custom-sets (met bestand) → overige items (JSON).
  var chain = postForm(mainFd);
  customForms.forEach(function (fd) { chain = chain.then(function () { return postForm(fd); }); });
  chain
    .then(function () {
      var items = self.addonItems(qty);
      if (!items.length) return null;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      });
    })
    .then(function () {
      if (btn) { btn.disabled = false; btn.textContent = original; }
      self.openCart();
    })
    .catch(function (err) {
      console.warn('[cfg] cart error:', err);
      if (btn) { btn.disabled = false; btn.textContent = original; }
    });
};

Configurator.prototype.openCart = function () {
  if (this.cartMode === 'minicart') { this.openMiniCart(); return; }
  // Horizon-drawer (BLNC)
  var cartBtn = document.querySelector('[aria-controls="cart-drawer"], [data-target="cart"]');
  if (cartBtn) { cartBtn.click(); return; }
  document.dispatchEvent(new CustomEvent('cart:open'));
  window.dispatchEvent(new CustomEvent('cart:refresh'));
  setTimeout(function () {
    var drawer = document.querySelector('cart-drawer, #cart-drawer');
    if (!drawer) window.location.href = '/cart';
  }, 800);
};

Configurator.prototype.openMiniCart = function () {
  // WW Focal-thema: mini-cart section verversen en openen
  var miniCart = document.getElementById('mini-cart');
  var sectionId = miniCart && miniCart.closest('[id^="shopify-section-"]')
    ? miniCart.closest('[id^="shopify-section-"]').id.replace('shopify-section-', '')
    : 'mini-cart';
  fetch('/?section_id=' + sectionId, { headers: { 'Accept': 'text/html' } })
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var mc = document.getElementById('mini-cart');
      if (mc) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('#mini-cart');
        if (fresh) mc.innerHTML = fresh.innerHTML;
      }
      var cartBtn = document.querySelector('[aria-controls="mini-cart"]');
      if (cartBtn) { cartBtn.click(); return; }
      var notif = document.querySelector('cart-notification');
      if (notif) { notif.hidden = false; return; }
      window.location.href = '/cart';
    })
    .catch(function () { window.location.href = '/cart'; });
};

function initAll() {
  document.querySelectorAll('#blnc-cfg, .blnc-cfg[data-uid]').forEach(function (root) {
    if (root.dataset.blncInit) return;
    root.dataset.blncInit = '1';
    new Configurator(root);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}
